import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lightbulb, ArrowLeft } from "phosphor-react";
import { useSharedProfile } from "../context/SharedProfileContext";
import { updateSeoMetadata } from "../lib/seo";

// Logo assets
const LOGO_VERTICAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/zc7oaxip_brain%20%2B%20GENICERTO.png";
const LOGO_PUREBRAIN = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/gtg4vc6a_purebrain.png";

export default function About() {
  const navigate = useNavigate();
  // Shared/Public profile mode - used for context awareness (no restrictions needed on About page)
  const { isSharedProfileView } = useSharedProfile();

  useEffect(() => {
    updateSeoMetadata({
      title: "About GENI — Trading Analytics Platform",
      description: "Learn about GENI, our mission to help traders turn trading data into better decisions with automated journaling and analytics.",
      path: "/about",
      lang: "en",
    });
  }, []);

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#0A0A0A] p-6 md:p-12" data-testid="about-page">
      <div className="w-full max-w-[680px] mx-auto">
        {!isSharedProfileView ? (
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-8 group"
            data-testid="about-back-button"
          >
            <ArrowLeft size={20} weight="bold" className="group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm font-medium">Back</span>
          </button>
        ) : null}

        {/* Hero Branding Section */}
        <div className={`flex flex-col items-center text-center mb-12 ${isSharedProfileView ? 'pt-0' : 'pt-4 md:pt-8'}`}>
          <img
            src={LOGO_VERTICAL}
            alt="GENI"
            className="h-24 md:h-28 lg:h-32 w-auto object-contain mb-6"
            data-testid="about-logo"
          />

          <h1 
            className="text-3xl md:text-5xl font-black font-heading tracking-tight text-white mb-4" 
            data-testid="about-title"
          >
            About GENI
          </h1>

          <p className="text-white/50 max-w-2xl text-base md:text-lg">
            Built by a trader, for traders. GENI helps you track, analyze, and improve your trading with clarity, discipline, and real data.
          </p>

          <p className="text-white/40 max-w-xl mt-3 text-sm md:text-base">
            This is not just a journal — it is a system designed to help traders understand what actually works.
          </p>
        </div>

        {/* Main Content */}
        <div className="space-y-12">
          
          {/* Section 1: Introduction */}
          <section>
            <p className="text-xl text-white/90 leading-relaxed">
              Geni is a trading assistant that removes the most boring and time-consuming 
              part of trading — <span className="text-white font-medium">manually tracking 
              and organizing your trades</span>.
            </p>
          </section>

          {/* Section 2: Value Proposition */}
          <section>
            <p className="text-white/70 leading-[1.8] text-base">
              It replaces scattered notes and endless spreadsheets with a clean, 
              automated system focused on what actually matters: <span className="text-white/90 font-medium">improvement</span>.
            </p>
          </section>

          {/* Section 3: Benefits */}
          <section className="py-8 px-6 bg-white/[0.02] rounded-xl border border-white/5">
            <p className="text-white/60 text-sm uppercase tracking-wider mb-6 font-medium">
              What Geni helps you do
            </p>
            <ul className="space-y-5">
              <li className="flex items-start gap-4">
                <span className="w-2 h-2 rounded-full bg-[#00E676] mt-2 shrink-0" />
                <span className="text-white/85 text-base leading-relaxed">
                  <span className="text-white font-medium">Track your trades effortlessly</span> — 
                  upload a chart, and Geni extracts the data for you
                </span>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-2 h-2 rounded-full bg-[#00E676] mt-2 shrink-0" />
                <span className="text-white/85 text-base leading-relaxed">
                  <span className="text-white font-medium">Analyze your performance with real data</span> — 
                  see what&apos;s working across setups, timeframes, and behavior
                </span>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-2 h-2 rounded-full bg-[#00E676] mt-2 shrink-0" />
                <span className="text-white/85 text-base leading-relaxed">
                  <span className="text-white font-medium">Identify what works — and what doesn&apos;t</span> — 
                  with insights you&apos;d miss looking at trades one by one
                </span>
              </li>
            </ul>
          </section>

          {/* Section 4: Big Picture */}
          <section className="space-y-6">
            <p className="text-white/70 leading-[1.8] text-base">
              Every trade you log becomes part of a bigger picture. Geni uses 
              statistical and behavioral insights to highlight patterns you 
              wouldn&apos;t easily see on your own.
            </p>
          </section>

          {/* Section 5: Goal Highlight Block */}
          <section className="mt-8">
            <div className="p-8 rounded-xl bg-gradient-to-br from-[#00E676]/[0.08] to-[#00E676]/[0.02] border border-[#00E676]/20 shadow-lg shadow-[#00E676]/5">
              <div className="flex items-start gap-5">
                <div className="p-3 rounded-xl bg-[#00E676]/15 shrink-0">
                  <Lightbulb size={28} weight="duotone" className="text-[#00E676]" />
                </div>
                <div>
                  <p className="text-white text-lg font-semibold mb-2">
                    The goal is simple:
                  </p>
                  <p className="text-white/80 text-base leading-relaxed">
                    Turn your trading data into <span className="text-white font-medium">clarity</span>, <span className="text-white font-medium">consistency</span>, and <span className="text-white font-medium">better decisions</span>.
                  </p>
                </div>
              </div>
            </div>
          </section>

        </div>

        {/* Footer Branding */}
        <div className="flex items-center justify-center gap-2 mt-16 mb-6 text-sm text-white/40">
          <img
            src={LOGO_PUREBRAIN}
            alt="GENI"
            className="h-5 w-auto object-contain opacity-80"
          />
          <span>
            Powered by <span className="text-white font-medium">GENI</span>
          </span>
        </div>
      </div>
    </div>
  );
}
